import axios from "axios";
import { apiRoot } from "../../index";
import { startLoading, stopLoading } from "../../utils/loader";

function getApiErrorMessage(error) {
  const responseData = error?.response?.data;
  return typeof responseData === "string"
    ? responseData
    : responseData?.error ||
        responseData?.message ||
        (responseData ? JSON.stringify(responseData) : "");
}

function showActionErrorAlert(error, defaultPrefix, pastDatePrefix = null) {
  const apiError = getApiErrorMessage(error);

  if (/upgrade to Invoicing Plus/i.test(apiError)) {
    window.alert(
      "Vous devez utiliser le plan 'Invoicing Plus' de Stripe : https://dashboard.stripe.com/settings/billing/automatic"
    );
    return;
  }

  if (pastDatePrefix && /est dans le passé/i.test(apiError)) {
    window.alert(`${pastDatePrefix}\n\nDétail : ${apiError}`);
    return;
  }

  window.alert(
    `${defaultPrefix} : ${apiError || error?.message || "Erreur inconnue"}`
  );
}

export function pdfBase64ToBlob(base64) {
  const bytes = atob(base64);
  let length = bytes.length;
  let out = new Uint8Array(length);

  while (length--) {
    out[length] = bytes.charCodeAt(length);
  }

  return new Blob([out], { type: "application/pdf" });
}

export function sendEstimateEmail(estimateId, jwt, selectedContacts = []) {
  startLoading();
  return new Promise((resolve, reject) => {
    axios
      .post(
        apiRoot + "/api/v1/dispatch/send-estimate-email",
        { 
          estimateId: estimateId,
          // Include selected contacts in the request
          // The backend API would need to be updated to handle this
          selectedContacts: selectedContacts 
        },
        {
          headers: {
            Authorization: jwt,
          },
        }
      )
      .then((response) => {
        stopLoading();
        resolve(response);
      })
      .catch((e) => {
        stopLoading();
        showActionErrorAlert(e, "Erreur lors de l'envoi du devis");
        reject(e);
      });
  });
}

export function getEstimatePdfBase64(estimateId, jwt) {
  return new Promise((resolve, reject) => {
    axios
      .post(
        apiRoot + "/api/v1/dispatch/get-estimate-pdf-base64",
        { estimateId: estimateId },
        {
          headers: {
            Authorization: jwt,
          },
        }
      )
      .then((response) => {
        resolve(response.data);
      })
      .catch((e) => {
        reject(e);
      });
  });
}

export function createInvoicesAndBookingsFromEstimateList(estimateIdList, jwt) {
  startLoading();
  return new Promise((resolve, reject) => {
    axios
      .post(
        apiRoot +
          "/api/v1/dispatch/invoice/create-invoices-and-bookings-from-estimates",
        { estimateIdList: estimateIdList },
        {
          headers: {
            Authorization: jwt,
          },
        }
      )
      .then((response) => {
        stopLoading();
        resolve(response);
      })
      .catch((e) => {
        stopLoading();
        showActionErrorAlert(
          e,
          "Erreur lors de la création de la facture et des courses depuis le devis",
          "Impossible de créer la facture et les courses : une ou plusieurs courses ont une date dans le passé."
        );
        reject(e);
      });
  });
}

export function duplicateEstimateFromEstimateIdList(estimateIdList, jwt) {
  startLoading();
  return new Promise((resolve, reject) => {
    axios
      .post(
        apiRoot + "/api/v1/dispatch/duplicate-estimates",
        { estimateIdList: estimateIdList },
        {
          headers: {
            Authorization: jwt,
          },
        }
      )
      .then((response) => {
        stopLoading();
        resolve(response);
      })
      .catch((e) => {
        stopLoading();
        if (e?.response?.data === "Invalid value for eu_vat.") {
          window.alert("Le numéro de TVA du client est invalide.");
        } else if (/upgrade to Invoicing Plus/i.test(e?.response?.data)) {
          window.alert(
            "Vous devez utiliser le plan 'Invoicing Plus' de Stripe : https://dashboard.stripe.com/settings/billing/automatic"
          );
        }
        reject(e);
      });
  });
}

export function createInvoiceFromBookingIdList(
  userInfos,
  bookingIdList,
  folderId = null
) {
  if (!userInfos.stripeAccountId) {
    window.alert('Please visit the "Estimate" or the "Invoice" page.');
    throw new Error("No stripe account id");
  }

  startLoading();
  return new Promise((resolve, reject) => {
    axios
      .post(
        apiRoot + "/api/v1/dispatch/create-invoice-from-booking-id-list",
        { bookingIdList, folderId },
        {
          headers: {
            Authorization: userInfos.jwt,
          },
        }
      )
      .then((response) => {
        stopLoading();
        resolve(response);
      })
      .catch((e) => {
        stopLoading();
        if (/upgrade to Invoicing Plus/i.test(e?.response?.data)) {
          window.alert(
            "Vous devez utiliser le plan 'Invoicing Plus' de Stripe : https://dashboard.stripe.com/settings/billing/automatic"
          );
        }
        reject(e);
      });
  });
}

export function createEstimateFromBookingIdList(
  userInfos,
  bookingIdList,
  folderId = null
) {
  // send email
  if (!userInfos.stripeAccountId) {
    window.alert('Please visit the "Estimate" or the "Invoice" page.');
    throw new Error("No stripe account id");
  }

  startLoading();
  return new Promise((resolve, reject) => {
    axios
      .post(
        apiRoot + "/api/v1/dispatch/create-estimate-from-booking-id-list",
        { bookingIdList, folderId },
        {
          headers: {
            Authorization: userInfos.jwt,
          },
        }
      )
      .then((response) => {
        stopLoading();
        resolve(response);
      })
      .catch((e) => {
        stopLoading();
        if (/upgrade to Invoicing Plus/i.test(e?.response?.data)) {
          window.alert(
            "Vous devez utiliser le plan 'Invoicing Plus' de Stripe : https://dashboard.stripe.com/settings/billing/automatic"
          );
        }
        reject(e);
      });
  });
}

export function sendInvoiceEmail(invoiceId, jwt, selectedContacts = []) {
  startLoading();

  return new Promise((resolve, reject) => {
    axios
      .post(
        apiRoot + "/api/v1/dispatch/send-invoice-email",
        { 
          invoiceId: invoiceId,
          // Include selected contacts in the request
          // The backend API would need to be updated to handle this
          selectedContacts: selectedContacts 
        },
        {
          headers: {
            Authorization: jwt,
          },
        }
      )
      .then((response) => {
        stopLoading();
        resolve(response);
      })
      .catch((e) => {
        stopLoading();
        if (/upgrade to Invoicing Plus/i.test(e?.response?.data)) {
          window.alert(
            "Vous devez utiliser le plan 'Invoicing Plus' de Stripe : https://dashboard.stripe.com/settings/billing/automatic"
          );
        }
        reject(e);
      });
  });
}

export function markInvoiceAsPaid(invoiceId, jwt) {
  startLoading();
  return new Promise((resolve, reject) => {
    axios
      .post(
        apiRoot + "/api/v1/dispatch/mark-invoice-as-paid",
        { invoiceId: invoiceId },
        {
          headers: {
            Authorization: jwt,
          },
        }
      )
      .then((response) => {
        stopLoading();
        resolve(response);
      })
      .catch((e) => {
        stopLoading();
        showActionErrorAlert(
          e,
          "Erreur lors du marquage de la facture",
          "Impossible de marquer la facture comme payée : une ou plusieurs courses ont une date dans le passé."
        );
        reject(e);
      });
  });
}

export function getInvoicePdfBase64(invoiceId, jwt) {
  return new Promise((resolve, reject) => {
    axios
      .post(
        apiRoot + "/api/v1/dispatch/get-invoice-pdf-base64",
        { invoiceId: invoiceId },
        {
          headers: {
            Authorization: jwt,
          },
        }
      )
      .then((response) => {
        resolve(response.data);
      })
      .catch((e) => {
        reject(e);
      });
  });
}

export function deleteEstimatesFromEstimateIdList(estimateIdList, jwt) {
  startLoading();
  return new Promise((resolve, reject) => {
    axios
      .post(
        apiRoot + "/api/v1/dispatch/delete-estimates-from-estimate-id-list",
        { estimateIdList: estimateIdList },
        {
          headers: {
            Authorization: jwt,
          },
        }
      )
      .then((response) => {
        stopLoading();
        resolve(response);
      })
      .catch((e) => {
        stopLoading();
        reject(e);
      });
  });
}

export function checkEstimatesExistOnStripe(estimateIdList, jwt) {
  return new Promise((resolve, reject) => {
    axios
      .post(
        apiRoot + "/api/v1/dispatch/check-estimates-exist-on-stripe",
        { estimateIdList: estimateIdList },
        {
          headers: {
            Authorization: jwt,
          },
        }
      )
      .then((response) => {
        resolve(response);
      })
      .catch((e) => {
        reject(e);
      });
  });
}

export function checkInvoicesExistOnStripe(invoiceIdList, jwt) {
  return new Promise((resolve, reject) => {
    axios
      .post(
        apiRoot + "/api/v1/dispatch/check-invoices-exist-on-stripe",
        { invoiceIdList: invoiceIdList },
        {
          headers: {
            Authorization: jwt,
          },
        }
      )
      .then((response) => {
        resolve(response);
      })
      .catch((e) => {
        reject(e);
      });
  });
}

export function deleteInvoicesFromInvoiceIdList(invoiceIdList, jwt) {
  startLoading();
  return new Promise((resolve, reject) => {
    axios
      .post(
        apiRoot + "/api/v1/dispatch/delete-invoices-from-invoice-id-list",
        { invoiceIdList: invoiceIdList },
        {
          headers: {
            Authorization: jwt,
          },
        }
      )
      .then((response) => {
        stopLoading();
        resolve(response);
      })
      .catch((e) => {
        stopLoading();
        reject(e);
      });
  });
}
